import { UserCircle, LogOut, Sun, Moon, Monitor } from "lucide-react";

import { useAuth } from "@/lib/auth/hooks";
import { ALL_ROLES, ROLE_LABEL } from "@/lib/auth/roles";
import { useAppDispatch, useAppSelector } from "@/store";
import { setMode } from "@/store/slices/theme";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const THEME_ICON = { light: Sun, dark: Moon, system: Monitor } as const;

export function UserMenu() {
  const { user, signOut, setRole } = useAuth();
  const dispatch = useAppDispatch();
  const themeMode = useAppSelector((s) => s.theme.mode);
  const Icon = THEME_ICON[themeMode];

  if (!user) return null;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="gap-2 rounded-full pr-3"
          aria-label="Account menu"
        >
          <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-muted">
            <UserCircle className="h-4 w-4" />
          </span>
          <span className="hidden text-sm font-medium sm:inline">{user.name}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[14rem]">
        <DropdownMenuLabel className="flex flex-col items-start gap-0.5 normal-case tracking-normal">
          <span className="text-sm font-semibold text-foreground">{user.name}</span>
          <span className="text-xs font-normal text-muted-foreground">{user.email}</span>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />

        <DropdownMenuLabel>Theme</DropdownMenuLabel>
        <DropdownMenuRadioGroup
          value={themeMode}
          onValueChange={(v) => dispatch(setMode(v as "light" | "dark" | "system"))}
        >
          <DropdownMenuRadioItem value="light">
            <Sun className="mr-2 h-4 w-4" /> Light
          </DropdownMenuRadioItem>
          <DropdownMenuRadioItem value="dark">
            <Moon className="mr-2 h-4 w-4" /> Dark
          </DropdownMenuRadioItem>
          <DropdownMenuRadioItem value="system">
            <Monitor className="mr-2 h-4 w-4" /> System
          </DropdownMenuRadioItem>
        </DropdownMenuRadioGroup>
        <DropdownMenuSeparator />

        {import.meta.env.DEV && (
          <>
            <DropdownMenuLabel>Dev role switcher</DropdownMenuLabel>
            <DropdownMenuRadioGroup
              value={user.role}
              onValueChange={(v) => setRole(v as typeof user.role)}
            >
              {ALL_ROLES.map((role) => (
                <DropdownMenuRadioItem key={role} value={role}>
                  {ROLE_LABEL[role]}
                </DropdownMenuRadioItem>
              ))}
            </DropdownMenuRadioGroup>
            <DropdownMenuSeparator />
          </>
        )}

        <DropdownMenuItem onSelect={signOut} className="gap-2">
          <LogOut className="h-4 w-4" />
          Sign out
        </DropdownMenuItem>

        {/* Hidden — keeps Icon var referenced to silence unused warnings */}
        <span className="sr-only">
          <Icon className="h-4 w-4" />
        </span>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
